import React, { useState, useCallback } from 'react';
import { Sparkles, Loader2, X, Plus, RefreshCw, CheckSquare, CheckCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import { generateAssessmentQuestions } from '@/services/PagesServices/FeedbackManagementService';
import type { AIGeneratedQuestion } from '@/services/PagesServices/FeedbackManagementService';
import type { LocalQuestion, AssessmentType, QuestionType, AIDifficulty } from '../types';
import { QUESTION_TYPES } from '../types';

interface AIAssistPanelProps {
  assessmentType: AssessmentType;
  objectiveRatio?: number;
  onAddQuestions: (questions: LocalQuestion[]) => void;
}

const getTypeLabel = (type: string): string =>
  QUESTION_TYPES.find(t => t.value === type)?.label || type;

const AIAssistPanel: React.FC<AIAssistPanelProps> = ({ assessmentType, objectiveRatio = 50, onAddQuestions }) => {
  const [showAIAssist, setShowAIAssist] = useState(false);
  const [topic, setTopic] = useState('');
  const [questionCount, setQuestionCount] = useState(10);
  const [difficulty, setDifficulty] = useState<AIDifficulty>('medium');
  const [generatedQuestions, setGeneratedQuestions] = useState<AIGeneratedQuestion[]>([]);
  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);

  const handleGenerate = useCallback(async () => {
    if (!topic.trim()) {
      toast.error('Please enter a topic for question generation');
      return;
    }

    setLoading(true);
    setAiError(null);
    setGeneratedQuestions([]);
    setSelectedIndices(new Set());

    const result = await generateAssessmentQuestions(topic.trim(), questionCount, difficulty, assessmentType, assessmentType === 'both' ? objectiveRatio : undefined);

    setLoading(false);

    if (result.success && result.data?.questions) {
      setGeneratedQuestions(result.data.questions);
      setSelectedIndices(new Set(result.data.questions.map((_: AIGeneratedQuestion, i: number) => i)));
    } else {
      setAiError(result.error?.message || 'Failed to generate questions');
    }
  }, [topic, questionCount, difficulty, assessmentType, objectiveRatio]);

  const handleToggleQuestion = useCallback((index: number) => {
    setSelectedIndices(prev => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    setSelectedIndices(prev =>
      prev.size === generatedQuestions.length
        ? new Set()
        : new Set(generatedQuestions.map((_: AIGeneratedQuestion, i: number) => i))
    );
  }, [generatedQuestions]);

  const handleAddSelected = useCallback(() => {
    const selected = generatedQuestions.filter((_: AIGeneratedQuestion, i: number) => selectedIndices.has(i));
    const newQuestions: LocalQuestion[] = selected.map((q: AIGeneratedQuestion, i: number) => ({
      id: `temp-ai-${Date.now()}-${i}`,
      type: q.type as QuestionType,
      text: q.text,
      required: true,
      options: q.options,
      correctAnswers: q.correctAnswers,
    }));

    onAddQuestions(newQuestions);
    toast.success(`${selected.length} question(s) added to the form`);
    setGeneratedQuestions([]);
    setSelectedIndices(new Set());
  }, [generatedQuestions, selectedIndices, onAddQuestions]);

  const handleRegenerateMore = useCallback(async (count: number) => {
    setLoading(true);
    setAiError(null);

    const result = await generateAssessmentQuestions(topic.trim(), count, difficulty, assessmentType, assessmentType === 'both' ? objectiveRatio : undefined);

    setLoading(false);

    if (result.success && result.data?.questions) {
      const existingCount = generatedQuestions.length;
      setGeneratedQuestions(prev => [...prev, ...result.data!.questions]);
      setSelectedIndices(prev => {
        const next = new Set(prev);
        result.data!.questions.forEach((_: AIGeneratedQuestion, i: number) => next.add(existingCount + i));
        return next;
      });
    } else {
      setAiError(result.error?.message || 'Failed to generate more questions');
    }
  }, [topic, difficulty, assessmentType, objectiveRatio, generatedQuestions.length]);

  return (
    <div className="admin-feedback-ai-assist-section">
      <div className="admin-feedback-ai-assist-header">
        <button
          className={`admin-feedback-ai-assist-toggle ${showAIAssist ? 'active' : ''}`}
          onClick={() => setShowAIAssist(!showAIAssist)}
        >
          <Sparkles size={18} />
          AI Assist
        </button>
        {showAIAssist && generatedQuestions.length > 0 && (
          <span className="admin-feedback-ai-badge">{generatedQuestions.length} generated</span>
        )}
      </div>

      {showAIAssist && (
        <div className="admin-feedback-ai-config">
          <div className="admin-feedback-form-field">
            <label>Topic / Subject *</label>
            <input
              type="text"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder="e.g., JavaScript ES6 Features, React Hooks, Python OOP"
              disabled={loading}
            />
          </div>

          <div className="admin-feedback-ai-config-row">
            <div className="admin-feedback-form-field" style={{ flex: '0 0 140px' }}>
              <label>No. of Questions</label>
              <input
                type="number"
                value={questionCount}
                onChange={(e) => setQuestionCount(Math.min(30, Math.max(5, parseInt(e.target.value) || 5)))}
                min={5}
                max={30}
                disabled={loading}
              />
            </div>

            <div className="admin-feedback-form-field" style={{ flex: 1 }}>
              <label>Difficulty Level</label>
              <div className="admin-feedback-ai-difficulty-pills">
                {(['easy', 'medium', 'hard', 'mixed'] as const).map((level) => (
                  <button
                    key={level}
                    className={`admin-feedback-ai-pill ${difficulty === level ? 'active' : ''} admin-feedback-ai-pill-${level}`}
                    onClick={() => setDifficulty(level)}
                    disabled={loading}
                  >
                    {level.charAt(0).toUpperCase() + level.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <button
            className="admin-feedback-ai-generate-btn"
            onClick={handleGenerate}
            disabled={loading || !topic.trim()}
          >
            {loading ? (
              <>
                <Loader2 size={16} className="admin-feedback-spinner" />
                Generating...
              </>
            ) : (
              <>
                <Sparkles size={16} />
                Generate Questions
              </>
            )}
          </button>

          {aiError && (
            <div className="admin-feedback-ai-error">
              <X size={14} />
              {aiError}
            </div>
          )}

          {generatedQuestions.length > 0 && (
            <div className="admin-feedback-ai-results">
              <div className="admin-feedback-ai-results-header">
                <button className="admin-feedback-ai-select-all" onClick={handleSelectAll}>
                  <CheckSquare size={14} />
                  {selectedIndices.size === generatedQuestions.length ? 'Deselect All' : 'Select All'}
                </button>
                <span className="admin-feedback-ai-selected-count">
                  {selectedIndices.size} of {generatedQuestions.length} selected
                </span>
              </div>

              <div className="admin-feedback-ai-questions-list">
                {generatedQuestions.map((q, index) => (
                  <div
                    key={index}
                    className={`admin-feedback-ai-question-card ${selectedIndices.has(index) ? 'selected' : ''}`}
                    onClick={() => handleToggleQuestion(index)}
                  >
                    <div className="admin-feedback-ai-question-check">
                      <input
                        type="checkbox"
                        checked={selectedIndices.has(index)}
                        onChange={() => handleToggleQuestion(index)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    <div className="admin-feedback-ai-question-body">
                      <div className="admin-feedback-ai-question-meta">
                        <span className={`admin-feedback-ai-difficulty-badge ${q.difficulty}`}>
                          {q.difficulty}
                        </span>
                        <span className="admin-feedback-ai-type-badge">
                          {q.type === 'multiple_choice_multi' ? 'Multi-Select' :
                           q.type === 'multiple_choice_single' ? 'Single Choice' :
                           q.type === 'paragraph' ? 'Paragraph' :
                           q.type === 'short_text' ? 'Short Text' :
                           getTypeLabel(q.type)}
                        </span>
                      </div>
                      <div className="admin-feedback-ai-question-text">{q.text}</div>
                      {q.options && q.correctAnswers && (
                        <div className="admin-feedback-ai-options-grid">
                          {q.options.map((opt, optIdx) => (
                            <div
                              key={optIdx}
                              className={`admin-feedback-ai-option ${q.correctAnswers!.includes(optIdx) ? 'correct' : ''}`}
                            >
                              {q.correctAnswers!.includes(optIdx) && <CheckCircle size={12} />}
                              {opt}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="admin-feedback-ai-actions">
                <button
                  className="admin-feedback-ai-action-btn primary"
                  onClick={handleAddSelected}
                  disabled={selectedIndices.size === 0}
                >
                  <Plus size={16} />
                  Add {selectedIndices.size} to Form
                </button>
                <button
                  className="admin-feedback-ai-action-btn secondary"
                  onClick={handleGenerate}
                  disabled={loading}
                >
                  <RefreshCw size={16} />
                  Regenerate All
                </button>
                <button
                  className="admin-feedback-ai-action-btn secondary"
                  onClick={() => handleRegenerateMore(5)}
                  disabled={loading}
                >
                  <Plus size={16} />
                  Generate 5 More
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default React.memo(AIAssistPanel);
